// ================================================
// CLARITY REPORTING — TERMS OF SERVICE PAGE
// ================================================

import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'May 13, 2026';
const COMPANY      = 'Clarity Reporting';
const DOMAIN       = 'clarityreporting.io';
const EMAIL        = 'support@clarityreporting.io';

export default function TermsOfServicePage() {
  return (
    <div style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#1a1a1a', minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 60px', borderBottom: '1px solid #e0e0e0'
      }}>
        <Link to="/" style={{
          fontSize: 13, fontWeight: 700, letterSpacing: 3,
          textTransform: 'uppercase', textDecoration: 'none', color: '#1a1a1a'
        }}>
          Clarity<span style={{ fontWeight: 300, opacity: 0.5 }}> Reporting</span>
        </Link>
        <Link to="/login" style={{ fontSize: 13, color: '#888', textDecoration: 'none' }}>
          Sign In
        </Link>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '60px 40px 80px' }}>

        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#888', marginBottom: 16 }}>
          Legal
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 700, marginBottom: 8, letterSpacing: -0.5 }}>
          Terms of Service
        </h1>
        <p style={{ color: '#888', fontSize: 14, marginBottom: 48 }}>
          Last updated: {LAST_UPDATED}
        </p>

        <Section title="1. Acceptance of Terms">
          <p>
            By accessing or using {COMPANY} at {DOMAIN} (the "Service"), you agree to be bound
            by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use
            the Service.
          </p>
          <p style={{ marginTop: 12 }}>
            We reserve the right to update these Terms at any time. Continued use of the Service
            after changes constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="2. Description of Service">
          <p>
            {COMPANY} provides SEO competitive intelligence reporting. The Service retrieves
            keyword ranking data from public search engines and optionally from your connected
            Google Search Console account, compares your rankings against competitor websites
            you specify, and delivers reports to your email address on a schedule you configure.
          </p>
        </Section>

        <Section title="3. Accounts">
          <p>
            You must create an account to use the Service. You are responsible for:
          </p>
          <ul>
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activity that occurs under your account</li>
            <li>Providing accurate and current information</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
          </ul>
          <p style={{ marginTop: 12 }}>
            You must be at least 18 years old to create an account.
          </p>
        </Section>

        <Section title="4. Subscriptions and Billing">
          <SubHeading>Subscription Plans</SubHeading>
          <p>
            The Service is offered on a subscription basis. Current plans and pricing are
            available at {DOMAIN}/pricing. We reserve the right to change pricing with 30 days
            notice to existing subscribers.
          </p>

          <SubHeading>Billing</SubHeading>
          <p>
            Subscriptions are billed monthly in advance. By providing payment information, you
            authorize us to charge your payment method on a recurring monthly basis until you
            cancel. Payments are processed by Authorize.net or PayPal.
          </p>

          <SubHeading>Cancellation</SubHeading>
          <p>
            You may cancel your subscription at any time from your account settings. Cancellation
            takes effect at the end of your current billing period. We do not provide refunds for
            partial months.
          </p>

          <SubHeading>Failed Payments</SubHeading>
          <p>
            If a payment fails, we will attempt to retry the charge. If payment cannot be
            collected, your account will be suspended until payment is resolved.
          </p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to any part of the Service</li>
            <li>Use automated means to access the Service beyond normal usage</li>
            <li>Resell or redistribute the Service without written permission</li>
            <li>Interfere with or disrupt the integrity or performance of the Service</li>
            <li>Submit false, misleading, or fraudulent information</li>
          </ul>
        </Section>

        <Section title="6. Google Search Console Integration">
          <p>
            If you connect your Google Search Console account, you authorize {COMPANY} to
            access your Search Console data on your behalf. This access is used solely to
            generate your reports. You can revoke this access at any time from your account
            settings or directly from your Google account permissions.
          </p>
          <p style={{ marginTop: 12 }}>
            Our use of Google API data complies with the
            {' '}<a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#1a1a1a' }}>
              Google API Services User Data Policy
            </a>.
          </p>
        </Section>

        <Section title="7. Data and Reports">
          <p>
            Reports generated by the Service are for your personal or business use only.
            SEO ranking data is sourced from public search engine results and DataForSEO.
            We make no warranty that ranking data is 100% accurate or complete.
          </p>
          <p style={{ marginTop: 12 }}>
            Agency plan subscribers may share reports with their clients as part of their
            normal business operations. White-label reports may be presented under the
            agency's own branding.
          </p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>
            The Service, including its design, code, and content, is owned by {COMPANY} and
            protected by copyright and other intellectual property laws. You may not copy,
            modify, distribute, or reverse engineer any part of the Service.
          </p>
          <p style={{ marginTop: 12 }}>
            Reports generated from your data belong to you. You retain ownership of any
            data you provide to the Service.
          </p>
        </Section>

        <Section title="9. Disclaimer of Warranties">
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
            ERROR-FREE, OR THAT RANKING DATA WILL BE ACCURATE OR COMPLETE.
          </p>
          <p style={{ marginTop: 12 }}>
            SEO rankings are determined by search engines and are outside our control. We
            make no guarantees about the accuracy or completeness of ranking data.
          </p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY.toUpperCase()} SHALL NOT BE LIABLE
            FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING
            FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU
            PAID US IN THE THREE MONTHS PRECEDING THE CLAIM.
          </p>
        </Section>

        <Section title="11. Indemnification">
          <p>
            You agree to indemnify and hold {COMPANY} harmless from any claims, damages, or
            expenses arising from your use of the Service, your violation of these Terms, or
            your violation of any rights of a third party.
          </p>
        </Section>

        <Section title="12. Termination">
          <p>
            We reserve the right to suspend or terminate your account at our discretion if you
            violate these Terms or engage in behavior harmful to other users or the Service.
            Upon termination, your right to use the Service ceases immediately.
          </p>
        </Section>

        <Section title="13. Governing Law">
          <p>
            These Terms are governed by the laws of the United States. Any disputes arising
            from these Terms or the Service shall be resolved through binding arbitration,
            except where prohibited by law.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>Questions about these Terms should be directed to:</p>
          <div style={{
            marginTop: 16, padding: '20px 24px',
            background: '#f5f5f5', fontSize: 14, lineHeight: 1.8
          }}>
            <strong>{COMPANY}</strong><br />
            {DOMAIN}<br />
            <a href={`mailto:${EMAIL}`} style={{ color: '#1a1a1a' }}>{EMAIL}</a>
          </div>
        </Section>

      </div>

      {/* Footer */}
      <footer style={{ padding: '28px 60px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888' }}>
        <Link to="/" style={{ color: '#888', textDecoration: 'none', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', fontSize: 11 }}>Clarity Reporting</Link>
        <div style={{ display: 'flex', gap: 24 }}>
          <Link to="/privacy" style={{ color: '#888', textDecoration: 'none' }}>Privacy Policy</Link>
          <Link to="/terms" style={{ color: '#888', textDecoration: 'none' }}>Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{
        fontSize: 16, fontWeight: 700, marginBottom: 16,
        paddingBottom: 10, borderBottom: '1px solid #e0e0e0'
      }}>
        {title}
      </h2>
      <div style={{ fontSize: 14, lineHeight: 1.8, color: '#444' }}>
        {children}
      </div>
    </div>
  );
}

function SubHeading({ children }) {
  return (
    <p style={{ fontWeight: 700, fontSize: 14, marginTop: 16, marginBottom: 8, color: '#1a1a1a' }}>
      {children}
    </p>
  );
}
